import React from "react"
import { useTranslation } from "react-i18next"
import { useNavigationStore } from "@/stores/navigation"
import CustomerLogos from "@/components/common/CustomerLogos"
import {
  CalendarDays,
  Workflow,
  Link as LinkIcon,
  Gauge,
  ShieldCheck,
  FileCheck,
  Lock,
  BarChart3,
  Building2,
  Microscope,
  Dna,
  FlaskConical,
  TestTube,
  Activity,
  AlertTriangle,
  BedDouble,
  ClipboardList,
} from "lucide-react"

const CellCX: React.FC = () => {
  const { t } = useTranslation()
  const navigationStore = useNavigationStore()

  // Icon mappings for supported workflows
  const workflowIcons = {
    patientFlow: Activity,
    labFlow: Microscope,
    clinicalTrialFlow: TestTube,
    admissionsPlanning: BedDouble,
  }

  // Icon mappings for features
  const featureIcons = {
    workflowEngine: Workflow,
    dashboards: BarChart3,
    chainOfIdentity: Dna,
    slasAlerts: AlertTriangle,
    scheduling: CalendarDays,
    integration: LinkIcon,
  }

  // Icon mappings for use cases
  const useCaseIcons = {
    carTBMT: Building2,
    clinicalTrials: ClipboardList,
    oncologyTransplant: Gauge,
    hospitalLabCoordination: Microscope,
  }

  // Icon mappings for FAQ
  const faqIcons = {
    integration: LinkIcon,
    security: ShieldCheck,
    implementation: FileCheck,
    pricing: BarChart3,
    cellserve: FlaskConical,
  }

  const handleBookDemo = () => {
    navigationStore.openDemoModal()
  }

  return (
    <div className="min-h-screen w-full text-white">
      {/* STICKY MOBILE CTA */}
      <div className="fixed bottom-0 inset-x-0 z-50 bg-white/90 backdrop-blur border-t border-slate-200 p-3 sm:hidden">
        <div className="flex gap-3">
          <button
            onClick={handleBookDemo}
            className="flex-1 text-center bg-[#395091] text-white font-semibold px-4 py-3 rounded-lg shadow-md hover:bg-[#2d3d6b] transition"
          >
            {t("common.bookDemo")}
          </button>
        </div>
      </div>

      {/* HERO + BODY COMBINED */}
      <main className="bg-white text-slate-800">
        <header
          className="w-full"
          style={{
            background: "linear-gradient(90deg, #395091 0%, #559EBA 100%)",
          }}
        >
          <div className="max-w-6xl mx-auto px-4 sm:px-6 py-16 sm:py-20 text-white">
            {/* Product Wordmark */}
            <div className="mb-8 sm:mb-10">
              <h2 className="text-6xl sm:text-7xl md:text-8xl leading-none tracking-tight">
                <span >cell</span>
                <span className="font-bold text-[#7AC943]">CX</span>
                <sup className="align-super text-2xl sm:text-3xl md:text-4xl ml-1">
                  ™
                </sup>
              </h2>
            </div>

            {/* Headline & Subcopy */}
            <div className="max-w-3xl">
              <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-3 sm:mb-4">
                {t("cellcx.hero.title")}
              </h1>
              <p className="text-base sm:text-lg md:text-xl/8 opacity-95">
                {t("cellcx.hero.subtitle")}
              </p>
              <div className="mt-6 sm:mt-8 flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
                <button
                  onClick={handleBookDemo}
                  className="w-full sm:w-auto text-center bg-white text-[#395091] font-semibold px-6 py-3 rounded-lg shadow-md hover:bg-gray-100 transition"
                >
                  {t("common.bookDemo")}
                </button>
                <a
                  href="#case-study"
                  className="w-full sm:w-auto text-center bg-white/10 backdrop-blur-sm border border-white/20 text-white font-semibold px-6 py-3 rounded-lg hover:bg-white/20 transition"
                >
                  {t("common.downloadCaseStudy")}
                </a>
              </div>
            </div>
          </div>
        </header>

        {/* Supported Workflows */}
        <section
          id="workflows"
          className="max-w-6xl mx-auto px-4 sm:px-6 py-12 sm:py-14"
        >
          <h3 className="text-2xl md:text-3xl font-bold text-slate-900 mb-2">
            {t("cellcx.supportedWorkflows.title")}
          </h3>
          <p className="text-slate-600 mb-6">
            {t("cellcx.supportedWorkflows.subtitle")}
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 sm:gap-6">
            {Object.entries(workflowIcons).map(([key, Icon]) => (
              <div
                key={key}
                className="rounded-2xl border border-slate-200 p-5 sm:p-6 shadow-sm hover:shadow-md transition bg-white/70 backdrop-blur-[1px]"
              >
                <div className="flex items-start gap-3 mb-2">
                  <span className="inline-flex items-center justify-center w-10 h-10 rounded-xl bg-[#F0F5FF]">
                    <Icon
                      className="w-5 h-5 text-[#395091]"
                      aria-hidden="true"
                    />
                  </span>
                  <h4 className="font-semibold text-slate-900 text-lg leading-snug">
                    {t(`cellcx.supportedWorkflows.items.${key}.title`)}
                  </h4>
                </div>
                <p className="text-slate-600 text-base leading-relaxed">
                  {t(`cellcx.supportedWorkflows.items.${key}.body`)}
                </p>
              </div>
            ))}
          </div>
        </section>

        {/* Features */}
        <section
          id="features"
          className="max-w-6xl mx-auto px-4 sm:px-6 py-12 sm:py-14 bg-gradient-to-br from-[#EDF7FF] to-[#F9FBFF] rounded-3xl"
        >
          <h3 className="text-2xl md:text-3xl font-bold text-slate-900 mb-2">
            {t("cellcx.features.title")}
          </h3>
          <p className="text-slate-600 mb-6">{t("cellcx.features.subtitle")}</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-6">
            {Object.entries(featureIcons).map(([key, Icon]) => (
              <div
                key={key}
                className="rounded-2xl bg-gradient-to-tr from-[#F0F5FF] to-white border border-slate-200 p-5 sm:p-6 shadow-sm"
              >
                <div className="flex items-start gap-3 mb-2">
                  <span className="inline-flex items-center justify-center w-10 h-10 rounded-xl bg-[#E3ECFF]">
                    <Icon
                      className="w-5 h-5 text-[#395091]"
                      aria-hidden="true"
                    />
                  </span>
                  <h4 className="font-semibold text-slate-900 text-lg leading-snug">
                    {t(`cellcx.features.items.${key}.title`)}
                  </h4>
                </div>
                <p className="text-slate-600 text-base leading-relaxed">
                  {t(`cellcx.features.items.${key}.body`)}
                </p>
              </div>
            ))}
          </div>
        </section>

        {/* Use Cases */}
        <section
          id="use-cases"
          className="max-w-6xl mx-auto px-4 sm:px-6 py-12 sm:py-14 bg-gradient-to-br from-[#EDF7FF] to-[#F9FBFF] rounded-3xl"
        >
          <h3 className="text-2xl md:text-3xl font-bold text-slate-900 mb-6 sm:mb-8">
            {t("cellcx.useCases.title")}
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 sm:gap-6">
            {Object.entries(useCaseIcons).map(([key, Icon]) => (
              <div
                key={key}
                className="rounded-2xl bg-gradient-to-tr from-[#F0F5FF] to-white border border-slate-200 p-5 sm:p-6 shadow-sm"
              >
                <div className="flex items-start gap-3 mb-2">
                  <span className="inline-flex items-center justify-center w-10 h-10 rounded-xl bg-[#E3ECFF]">
                    <Icon
                      className="w-5 h-5 text-[#395091]"
                      aria-hidden="true"
                    />
                  </span>
                  <h4 className="font-semibold text-slate-900 text-lg leading-snug">
                    {t(`cellcx.useCases.items.${key}.title`)}
                  </h4>
                </div>
                <p className="text-slate-600 text-base leading-relaxed">
                  {t(`cellcx.useCases.items.${key}.body`)}
                </p>
              </div>
            ))}
          </div>
        </section>

        {/* Partnership */}
        <section
          id="cellserve"
          className="max-w-6xl mx-auto px-4 sm:px-6 py-12 sm:py-14 bg-white"
        >
          <div className="rounded-2xl border border-slate-200 p-5 sm:p-6 shadow-sm">
            <div className="flex items-center gap-3 mb-2">
              <FlaskConical className="w-6 h-6 text-[#395091]" />
              <h3 className="text-2xl md:text-3xl font-bold text-slate-900">
                {t("cellcx.partnership.title")}
              </h3>
            </div>
            <p className="text-slate-700 leading-relaxed">
              {t("cellcx.partnership.body")}
            </p>
          </div>
        </section>

        {/* Case Study + Why */}
        <section
          id="case-study"
          className="max-w-6xl mx-auto px-4 sm:px-6 py-12 sm:py-14 scroll-mt-24 md:scroll-mt-28"
        >
          <div className="grid lg:grid-cols-2 gap-8 sm:gap-10 items-start">
            {/* Case Study */}
            <div>
              <h3 className="text-2xl md:text-3xl font-bold text-slate-900 mb-3 sm:mb-4">
                {t("cellcx.caseStudy.title")}
              </h3>
              <p className="text-slate-600 leading-relaxed mb-4">
                {t("cellcx.caseStudy.description")}
              </p>
              <div className="flex flex-col sm:flex-row gap-3 sm:items-center">
                <a
                  href="/use-case-finder?Org=DFCI"
                  className="inline-block rounded-lg bg-[#395091] text-white px-5 py-3 font-semibold hover:opacity-90 text-center"
                >
                  {t("common.viewCaseStudy")}
                </a>
                <a
                  href="/use-case-finder"
                  className="inline-block text-[#395091] font-semibold hover:underline text-center"
                >
                  {t("cellcx.caseStudy.seeMoreStories")}
                </a>
              </div>
              {/* Client logos row */}
              <CustomerLogos logoUids={["rady", "stlukes", "nhs"]} />
            </div>

            {/* Why CellCX */}
            <div className="rounded-2xl border border-slate-200 p-5 sm:p-6 shadow-sm bg-gradient-to-br from-[#EDF2FF] via-white to-[#F9FAFB]">
              <h4 className="font-bold text-slate-900 mb-3 text-3xl md:text-4xl">
                {t("cellcx.whyCellcx.title")}
              </h4>
              <p className="text-slate-700 text-base leading-relaxed mb-4">
                {t("cellcx.whyCellcx.description")}
              </p>
              <ul className="space-y-3 text-base">
                {Object.entries(featureIcons)
                  .slice(0, 3)
                  .map(([key, Icon], idx) => (
                    <li key={idx}>
                      <details className="group">
                        <summary className="cursor-pointer font-medium text-slate-900 flex items-center gap-2">
                          <Icon className="w-5 h-5 text-[#395091]" />
                          {t(`cellcx.features.items.${key}.title`)}
                        </summary>
                        <div className="mt-2 text-slate-600">
                          <p>{t("cellcx.whyCellcx.learnMore")}</p>
                        </div>
                      </details>
                    </li>
                  ))}
              </ul>
            </div>
          </div>
        </section>

        {/* Trust & Compliance */}
        <section className="max-w-6xl mx-auto px-4 sm:px-6 py-12 sm:py-14 border-t border-slate-200">
          <h3 className="text-2xl md:text-3xl font-bold text-slate-900 mb-6">
            {t("cellcx.trust.title")}
          </h3>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-6 text-center">
            <div className="flex flex-col items-center gap-2">
              <ShieldCheck className="w-8 h-8 text-[#395091]" />
              <span className="text-slate-700 font-medium">
                {t("cellcx.trust.hipaa")}
              </span>
            </div>
            <div className="flex flex-col items-center gap-2">
              <FileCheck className="w-8 h-8 text-[#395091]" />
              <span className="text-slate-700 font-medium">
                {t("cellcx.trust.soc2")}
              </span>
            </div>
            <div className="flex flex-col items-center gap-2">
              <Lock className="w-8 h-8 text-[#395091]" />
              <span className="text-slate-700 font-medium">
                {t("cellcx.trust.sso")}
              </span>
            </div>
            <div className="flex flex-col items-center gap-2">
              <ShieldCheck className="w-8 h-8 text-[#395091]" />
              <span className="text-slate-700 font-medium">
                {t("cellcx.trust.gdpr")}
              </span>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="max-w-6xl mx-auto px-4 sm:px-6 py-12 sm:py-14 border-t border-slate-200">
          <h3 className="text-2xl md:text-3xl font-bold text-slate-900 mb-6">
            {t("cellcx.faq.title")}
          </h3>
          <ul className="space-y-4">
            {Object.entries(faqIcons).map(([key, Icon], i) => (
              <li key={i} className="border border-slate-200 rounded-lg p-4">
                <details className="group">
                  <summary className="flex items-center gap-2 font-medium cursor-pointer text-slate-900">
                    <Icon className="w-5 h-5 text-[#395091]" />
                    {t(`cellcx.faq.items.${key}.question`)}
                  </summary>
                  <p className="mt-2 text-slate-600 leading-relaxed">
                    {t(`cellcx.faq.items.${key}.answer`)}
                  </p>
                </details>
              </li>
            ))}
          </ul>
        </section>

        {/* Final CTA */}
        <section id="contact" className="relative mt-6 sm:mt-8">
          <div
            className="absolute inset-0"
            style={{
              background: "linear-gradient(90deg, #395091 0%, #559EBA 100%)",
            }}
          />
          <div className="relative max-w-6xl mx-auto px-4 sm:px-6 py-12 sm:py-14 text-white">
            <div className="grid lg:grid-cols-2 gap-8 sm:gap-10 items-center">
              <div>
                <h3 className="text-2xl sm:text-3xl font-bold mb-2 sm:mb-3">
                  {t("cellcx.cta.title")}
                </h3>
                <p className="opacity-90 text-base sm:text-lg">
                  {t("cellcx.cta.subtitle")}
                </p>
              </div>
              <div className="flex flex-col sm:flex-row gap-3 lg:justify-end w-full sm:w-auto">
                <button
                  onClick={handleBookDemo}
                  className="w-full sm:w-auto text-center bg-white text-[#395091] font-semibold px-6 py-3 rounded-lg shadow-md hover:bg-gray-100 transition"
                >
                  {t("common.bookDemo")}
                </button>
              </div>
            </div>
          </div>
        </section>

        {/* Footer note */}
        <section className="max-w-6xl mx-auto px-4 sm:px-6 py-8 sm:py-10 text-slate-500 text-sm">
          {t("cellcx.footer")}
        </section>
      </main>
    </div>
  )
}

export default CellCX
